import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Clock3, Image as ImageIcon, X } from "lucide-react";
import { toast } from "sonner";
import { useUser } from "../context/UserContext";
import { getPrivatePhotoBlob, getPrivatePhotos } from "../lib/api";
import PageWrapper from "./PageWrapper";

const remainingLabel = (expiresAt) => {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "Expire bientôt";
  const totalMinutes = Math.floor(ms / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours > 0) return `Expire dans ${hours} h ${minutes} min`;
  return `Expire dans ${Math.max(minutes, 1)} min`;
};

export default function PhotosView() {
  const { currentUserId } = useUser();
  const [photos, setPhotos] = useState(null);
  const [selected, setSelected] = useState(null);
  const [imageUrl, setImageUrl] = useState(null);
  const [loadingImage, setLoadingImage] = useState(false);

  const selectedMeta = useMemo(
    () => photos?.find((photo) => photo.id === selected) || null,
    [photos, selected]
  );

  useEffect(() => {
    if (!currentUserId) return;
    getPrivatePhotos(currentUserId)
      .then(setPhotos)
      .catch(() => setPhotos([]));
  }, [currentUserId]);

  useEffect(() => {
    let cancelled = false;
    let objectUrl = null;

    if (!selected || !currentUserId) {
      setImageUrl(null);
      return undefined;
    }

    setLoadingImage(true);
    getPrivatePhotoBlob(selected, currentUserId)
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setImageUrl(objectUrl);
      })
      .catch((e) => {
        if (!cancelled) {
          toast.error(e?.response?.data?.detail || "Cette photo n'est plus disponible");
          setSelected(null);
          getPrivatePhotos(currentUserId).then(setPhotos).catch(() => {});
        }
      })
      .finally(() => {
        if (!cancelled) setLoadingImage(false);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [selected, currentUserId]);

  return (
    <PageWrapper>
      <div className="private-photos-page" data-testid="private-photos-page">
        <p className="page-eyebrow">Éphémère</p>
        <h1 className="page-title">Photos privées</h1>
        <p className="page-note">
          Les photos sont accessibles uniquement depuis ton compte et sont supprimées automatiquement 24 h après leur envoi.
        </p>

        {photos === null ? (
          <div className="center-note">Chargement…</div>
        ) : photos.length === 0 ? (
          <div className="empty-state photo-empty-state">
            <ImageIcon size={30} strokeWidth={1.3} />
            <h2 className="empty-title">Rien à voir pour l'instant</h2>
            <p className="empty-text">Une photo reçue après un défi apparaîtra ici pendant 24 heures.</p>
          </div>
        ) : (
          <div className="private-photo-list">
            {photos.map((photo) => (
              <button
                key={photo.id}
                className="private-photo-card"
                onClick={() => setSelected(photo.id)}
              >
                <span className="private-photo-icon"><ImageIcon size={22} strokeWidth={1.4} /></span>
                <span className="private-photo-copy">
                  <strong>Photo privée reçue</strong>
                  <small>{photo.challenge?.text}</small>
                  <em><Clock3 size={13} /> {remainingLabel(photo.expiresAt)}</em>
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selectedMeta && (
        <motion.div
          className="private-photo-overlay"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onContextMenu={(event) => event.preventDefault()}
        >
          <button className="private-photo-close" onClick={() => setSelected(null)} aria-label="Fermer">
            <X size={24} />
          </button>
          <div className="private-photo-viewer">
            {loadingImage ? (
              <div className="center-note">Ouverture…</div>
            ) : imageUrl ? (
              <img
                src={imageUrl}
                alt="Photo privée temporaire"
                draggable="false"
                onDragStart={(event) => event.preventDefault()}
              />
            ) : null}
            <div className="private-photo-viewer-meta">
              <p>{selectedMeta.challenge?.text}</p>
              <span><Clock3 size={14} /> {remainingLabel(selectedMeta.expiresAt)}</span>
            </div>
          </div>
        </motion.div>
      )}
    </PageWrapper>
  );
}
